import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { AccountBalanceRounded, ContentCopyRounded, UploadFileRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { BankTransferDetails } from '@dosfilos/domain';
import { tuitionApi } from '../../../lib/api/tuition.api';

// ─── Cómo pagar por transferencia (SEC-3) ───────────────────────────────────
// Los datos son de CADA seminario (Configuración → Identidad, «Pagos por
// transferencia») y se piden al servidor, que solo los da a los miembros de ese
// seminario. Antes estaban escritos en el código y los veían los alumnos de
// todos los seminarios. Sin datos configurados, el bloque no se muestra.

const ROWS: { key: keyof BankTransferDetails; copy?: boolean }[] = [
  { key: 'holder' },
  { key: 'taxId' },
  { key: 'bank' },
  { key: 'accountType' },
  { key: 'accountNumber', copy: true },
  { key: 'email', copy: true },
];

export function PaymentInfoDialog({
  open,
  tenantId,
  onClose,
  onRegister,
}: {
  open: boolean;
  tenantId: string | null;
  onClose: () => void;
  onRegister: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'tuition.paymentInfo';
  const [copied, setCopied] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['payment-instructions', tenantId],
    enabled: open && !!tenantId,
    staleTime: 5 * 60 * 1000,
    queryFn: () => tuitionApi.getPaymentInstructions({ tenantId: tenantId! }),
  });
  const bank = query.data?.bankTransfer ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#161b22', border: '1px solid #30363d', borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccountBalanceRounded sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="white">
              {t(`${k}.title`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(`${k}.subtitle`)}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2}>
          {query.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {bank && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.03)',
                borderColor: '#30363d',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <AccountBalanceRounded sx={{ color: 'primary.light', fontSize: 18 }} />
                <Typography variant="body2" fontWeight={700} color="white">
                  {t(`${k}.bankTitle`)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {ROWS.filter((r) => bank[r.key]).map((r) => (
                  <Stack
                    key={r.key}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 0.4, borderBottom: '1px solid #21262d' }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 96, flexShrink: 0 }}
                    >
                      {t(`${k}.fields.${r.key}`)}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="white"
                        textAlign="right"
                      >
                        {bank[r.key]}
                      </Typography>
                      {r.copy && (
                        <Tooltip title={copied === r.key ? t(`${k}.copied`) : t(`${k}.copy`)}>
                          <IconButton
                            size="small"
                            aria-label={t(`${k}.copy`)}
                            sx={{
                              p: 0.3,
                              color: copied === r.key ? 'success.main' : 'text.disabled',
                            }}
                            onClick={() => {
                              void navigator.clipboard.writeText(bank[r.key]);
                              setCopied(r.key);
                              setTimeout(() => setCopied(null), 2000);
                            }}
                          >
                            <ContentCopyRounded sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
          {!query.isLoading && !bank && (
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              {t(`${k}.notConfigured`)}
            </Alert>
          )}
          <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
            {t(`${k}.afterPaying`)}
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onRegister}
          startIcon={<UploadFileRounded />}
          size="small"
        >
          {t(`${k}.register`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
